import { useDispatch } from "react-redux";
import { ServiceChargeInvoicingSteps } from "../../ServiceChargeInvoiceWizardSteps";
import { WizardStepTitle } from "app/modules/shared/wizard/WizardStepTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faWarning } from "@fortawesome/pro-solid-svg-icons";
import { WizardStepperFooter } from "app/modules/shared/wizard/WizardStepFooter";
import {
  getServiceChargeInvoiceProcessSelectionModel,
  selectServiceChargeInvoice,
  setCurrentStep,
} from "../../ServiceChargeInvoicingSlice";
import { useSelector } from "react-redux";
import {
  calculateDevelopmentChargesPreListBackgroundProcess,
  selectGeneratingDevelopmentChargesPreList,
  selectDevelopmentCharges,
  selectRunDevelopmentChargesCalculation,
  setDevelopmentCharges,
  setSelectedDevelopmentCharges,
} from "./DevelopmentChargesPreListSlice";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { createColumnHelper } from "@tanstack/table-core";
import { DevelopmentChargesModel } from "services/api/cplapi/CPLAPI";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { MultiSelectCheckbox } from "cpl-react-common/components/Inputs/MultiSelectCheckbox";
import { useEffect, useState } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { setRunGenerateInvoicePreviews } from "../InvoicePreview/InvoicesPreviewSlice";
import { OKAlert } from "cpl-react-common/components/Alerts/Okay";
import { Style } from "cpl-react-common/Helpers/AlertHelpers";

const DevelopmentChargePreList: React.FC = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const runCalculation = useSelector(selectRunDevelopmentChargesCalculation);
  const generatingPreList = useSelector(selectGeneratingDevelopmentChargesPreList);
  const invoices = useSelector(selectDevelopmentCharges);
  const serviceChargeInvoice = useSelector(selectServiceChargeInvoice);
  const [selectedRows, setSelectedRows] = useState<object[]>([]);
  const [showMultiplePeriodWarning, setShowMultiplePeriodWarning] = useState(false);
  const [impactedFundSchedules, setImpactedFundSchedules] = useState<string[]>([]);

  useEffect(() => {
    if (runCalculation) {
      thunkDispatch(calculateDevelopmentChargesPreListBackgroundProcess());
    }
  }, [runCalculation, thunkDispatch]);

  useEffect(() => {
    if (!runCalculation && !generatingPreList) thunkDispatch(getServiceChargeInvoiceProcessSelectionModel());
  }, [runCalculation, generatingPreList, thunkDispatch]);

  useEffect(() => {
    dispatch(setDevelopmentCharges(serviceChargeInvoice?.developmentChargesPreList));
  }, [dispatch, serviceChargeInvoice]);

  useEffect(() => {
    dispatch(setSelectedDevelopmentCharges(selectedRows));
  }, [dispatch, selectedRows]);

  const columnHelper = createColumnHelper<DevelopmentChargesModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "Actions",
      cell: (props) => (
        <div className="form-check form-check-sm form-check-custom form-check-solid">
          <input
            data-test-id={`pre-list-step__select-row-checkbox-${props.row.original.preListID}`}
            className="form-check-input"
            type="checkbox"
            checked={props.row.getIsSelected()}
            onChange={props.row.getToggleSelectedHandler()}
          />
        </div>
      ),
      header: (props) => (
        <MultiSelectCheckbox
          data-test-id="pre-list-step__select-all-checkbox"
          checked={props.table.getIsAllRowsSelected()}
          indeterminate={props.table.getIsSomeRowsSelected()}
          handleChange={props.table.toggleAllRowsSelected}
        />
      ),
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.developmentName, {
      id: "Development Name",
      cell: (info) => (
        <div title={info.row.original.hasMultipleBillingPeriods ? "Multiple billing periods present" : ""}>
          {info.getValue()}
          {info.row.original.hasMultipleBillingPeriods ? (
            <FontAwesomeIcon className="px-2" style={{ color: "#ffc700" }} size="sm" icon={faWarning} />
          ) : null}
        </div>
      ),
      header: () => <>Development Name</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.developmentNumber, {
      id: "Development Number",
      cell: (info) => info.getValue(),
      header: () => <>Development Number</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.propertyManager, {
      id: "Property Manager",
      cell: (info) => info.getValue(),
      header: () => <>Property Manager</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.description, {
      id: "Description",
      cell: (info) => info.getValue(),
      header: () => <>Description</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.fromDate, {
      id: "From Date",
      cell: (info) => info.getValue()?.toCPLUserFriendlyDateString(),
      header: () => <>From Date</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.toDate, {
      id: "To Date",
      cell: (info) => info.getValue()?.toCPLUserFriendlyDateString(),
      header: () => <>To Date</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.overdueDate, {
      id: "Overdue Date",
      cell: (info) => info.getValue()?.toCPLUserFriendlyDateString(),
      header: () => <>Overdue Date</>,
      meta: {
        columnClassName: "showTableCellOnTabletAndDesktop",
      },
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.total, {
      id: "Total",
      cell: (info) => (
        <>
          {info.row.original.hasNegativeAmounts ? (
            <span title="Contains negative amounts">
              <FontAwesomeIcon className="px-2" style={{ color: "#ffc700" }} size="sm" icon={faWarning} />
            </span>
          ) : null}
          {info.getValue()?.toGBPString()}
        </>
      ),
      header: () => <>Fund Total</>,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
      enableColumnFilter: false,
    }),
  ];

  function goToPreviousStep() {
    dispatch(setCurrentStep(ServiceChargeInvoicingSteps.GraceDays));
  }

  function handleContinueClick() {
    const selectedCharges = selectedRows as DevelopmentChargesModel[];
    const chargesWithMultiplePeriods = selectedCharges.filter((charge) => charge.hasMultipleBillingPeriods);

    if (chargesWithMultiplePeriods.length > 0) {
      const uniqueDescriptions = Array.from(
        new Set(chargesWithMultiplePeriods.flatMap((charge) => charge.description || []))
      ).sort();

      setImpactedFundSchedules(uniqueDescriptions);
      setShowMultiplePeriodWarning(true);
    } else {
      goToNextStep();
    }
  }

  function goToNextStep() {
    setShowMultiplePeriodWarning(false);
    dispatch(setRunGenerateInvoicePreviews(true));
    dispatch(setCurrentStep(ServiceChargeInvoicingSteps.GenerateInvoices));
  }

  return (
    <>
      <WizardStepTitle
        stepNumber={ServiceChargeInvoicingSteps.DevelopmentChargesPreList}
        title="Pre List"
        subtitle={"Please select the developments and fund schedules that you would like to generate invoices for"}
      ></WizardStepTitle>
      <ActivityIndicator isBusy={generatingPreList} busyMessage="Generating your pre-list">
        <CPLTable data={invoices} columns={columnDefinition} handleRowSelectionChange={setSelectedRows} />
        {!generatingPreList && invoices?.length < 1 && (
          <div>There are no development invoices for these parameters. Please go back and modify your selections</div>
        )}
      </ActivityIndicator>
      <WizardStepperFooter>
        <div className="d-flex justify-content-between">
          <div>
            <button
              data-test-id="pre-list-step__go-back-button"
              className="btn btn-danger btn-sm"
              type="button"
              onClick={() => goToPreviousStep()}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span className="ms-1">Go back</span>
            </button>
          </div>
          <div>
            <button
              data-test-id="pre-list-step__continue-button"
              className="btn btn-success btn-sm"
              type="button"
              onClick={() => handleContinueClick()}
              disabled={selectedRows.length < 1}
            >
              <span className="me-1">Continue</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>
      </WizardStepperFooter>

      <OKAlert
        showAlert={showMultiplePeriodWarning}
        alertText={
          <>
            <p>
              The selected <strong>{serviceChargeInvoice.chargesTo?.toCPLUserFriendlyDateString()}</strong> date spans
              multiple Fund Schedule Billing Periods.<br></br> Only the first eligible period will be processed in this
              run.
            </p>
            <p>
              Fund Schedules Impacted: <br></br>
              <strong>{impactedFundSchedules}</strong>
            </p>
            <p>
              Please complete this run, then run the process again using the same Capture Charges To date, Developments
              or Property Managers and Grace Days, to generate subsequent periods.
            </p>
          </>
        }
        alertTitle="Warning"
        callBack={() => {
          setShowMultiplePeriodWarning(false);
          goToNextStep();
        }}
        style={Style.Warning}
      />
    </>
  );
};

export { DevelopmentChargePreList };
